import React, { useEffect, useState } from 'react';
import { KompisSummaryPageController } from '@inter-ikea-kompis/react-components';
import SummarySafetyInstructions from 'ui/images/SummarySafetyInstructions.jpg';
import { ProductRowProductMeasureEnum } from '@inter-ikea-kompis/component-product-row';
import {
  SummaryPageConfigurationMeasureEnum,
  ISummaryPageControllerDesignConfiguration,
  PrintableContentEnum
} from '@inter-ikea-kompis/component-summary-page';
import {
  AddonComponent,
  HelpLinesComponentVisible,
  HelpLinesSystem,
  Part,
  ProductInfoComponent
} from '@inter-ikea-storageone/range';
import { getShoppingItemsInScene } from 'ducks/products';
import { useSelector } from 'react-redux';
import { useScreenshot } from 'contexts/screenshot';
import styled from 'styled-components';
import Box from 'ui/Box';
import Header from 'components/Header/Header';
import { useIntl } from 'react-intl';
import { isKiosk, isMobile, isProduction } from 'utils/platform';
import { useServiceSettings } from 'contexts/serviceSettings';
import { AppRoutes } from 'routes';
import {
  getPrintOptions,
  getAgeGroup,
  isConfigurationPossible,
  setPrintOptions,
  lastSavedConfiguration
} from 'ducks/layout';
import { useCore } from 'contexts/core';
import { NavisionExport } from '@inter-ikea-kompis/navision-export';
import { SalesSystemEnum, MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import RtlStyles from 'i18n/rtl';
import { SortListOptionsEnum } from '@inter-ikea-kompis/component-sort-list';
import AssemblyInstructions from 'ui/AssemblyInstructions/AssemblyInstructions';
import {
  getDesignLink,
  getGoodToKnowInformation,
  getMeasurements,
  getVpcFromSummaryUrl,
  trackAnalytics
} from './utils';
import { useAvailability } from 'contexts/availability';
import { getVpcCode, getVpcImageUrl, saveVpcCode } from 'ducks/vpc';
import { useVisibleOnScroll } from 'views/utils';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import { usePlannerConfig } from 'contexts/plannerConfig';
import {
  e2e,
  getRange,
  rangeIsBror,
  rangeIsIvar,
  rangeIsKnoxhult,
  rangeIsSmastad,
  stripURLOfQueryParameters
} from 'utils/queryUtils';
import PlatsaSoftClosingAssemblyInstruction from 'ui/PlatsaSoftClosingAssemblyInstruction/PlatsaSoftClosingHingeAssemblyInstruction';
import ExportScene from './ExportScene';
import Menu from 'components/Header/Menu/Menu';
import ShoppingButtons from './ShoppingButtons';
import WarningBanner from 'ui/WarningBanner/WarningBanner';
import { adjustMeasurementsSystemSummary } from 'utils/adjustMeasurementsSystem';
import { getAssemblyInstructions } from 'ducks/assemblyInstructions';
import ContentMargins from 'components/ContentMargins';
import { useTestFeature } from 'optimizely/hooks';
import useHideLoadingScreen from 'hooks/useHideLoadingScreen';
import { createDesignConfiguration } from 'utils/designConfiguration';
import { useAppDispatch } from 'ducks/hooks';
import { showPrintQuestionSheet } from 'ducks/sheets';
import { IShoppingProduct } from '@inter-ikea-kompis/types';
import { useSceneHandler } from 'contexts/sceneHandler';
import { getDesignId, saveDesignId } from 'ducks/designId';
import { getMappedConfiguration } from 'components/SaveDesignToProfile/SaveDesignToProfile';
import AssemblyInstructionsKitchen from 'ui/AssemblyInstructions/AssemblyInstructionsKitchen';

export interface ProductWithParts {
  parts: Part[];
}

const Page = styled.div``;

const PageContainer = styled.div`
  margin-bottom: 2rem;
`;

type Props = {
  fromConfigurationLoader?: boolean;
  hideBackButton?: boolean;
  backButtonTarget?: string;
};

const Summary = ({ fromConfigurationLoader, hideBackButton, backButtonTarget }: Props) => {
  const { formatMessage } = useIntl();
  const {
    rangeName,
    kompisSettings,
    dexfSettings: {
      general: { bookAppointmentEnabled, enableSaveToProfile },
      localisation: { useMetricMeasures, currencyCode },
      kompis: {
        localization: { salesSystem }
      }
    },
    translations
  } = useServiceSettings();
  const vpcCode = useSelector(getVpcCode);
  const productsInScene = useSelector(getShoppingItemsInScene);
  const coreApi = useCore();
  const { storeId } = useAvailability();
  const dispatch = useAppDispatch();
  const { visible, onScroll } = useVisibleOnScroll();
  const { plannerConfig } = usePlannerConfig();
  const disabledFeatures = plannerConfig.disabledFeatures;
  const configurationPossible = useSelector(isConfigurationPossible);
  const productInstructions = useSelector(getAssemblyInstructions);
  const vpcImage = useSelector(getVpcImageUrl);
  const useAssemblyService = useTestFeature('SummaryAssemblyService');
  const [assemblyTabTriggered, setAssemblyTabTriggered] = useState(false);
  const currentProppingAgeGroup = useSelector(getAgeGroup);
  const prevSavedConfiguration = useSelector(lastSavedConfiguration);
  const { setLocalScene } = useSceneHandler();
  const designId = useSelector(getDesignId);
  const newSummaryPageDesign = !!useTestFeature('NewSummaryPageDesign');
  const helpLinesSystem = coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem');
  const scene = coreApi.entities.getScene();
  const helpLinesVisible = scene?.getComponent<HelpLinesComponentVisible>(
    HelpLinesComponentVisible.id
  );
  const wallShape = helpLinesVisible
    ? {
        dimensions: helpLinesSystem?.getShapeDimensions(),
        type: helpLinesSystem?.currentShapeType
      }
    : undefined;
  const printOptions = useSelector(getPrintOptions);

  const goodToKnow = getGoodToKnowInformation(productsInScene, formatMessage, coreApi);

  const { images, vpcImages, captureVPCImages } = useScreenshot();

  const { shoppingProducts, shoppingItems, shoppingItemsWithPrice } = useFetchShoppingProducts(
    productsInScene
  );
  const [updatedShoppingItems, setUpdateShoppingItems] = useState(null);
  const [designSaved, setDesignSaved] = useState<boolean>(false);

  useHideLoadingScreen();

  const vpcCodeToShow = vpcCode ?? getVpcFromSummaryUrl();

  let enableAssemblyInstructions =
    configurationPossible &&
    !fromConfigurationLoader &&
    (productInstructions.length > 0 || goodToKnow.length > 0);

  // If we have specified a summaryFeatures object we want to make sure that this feature is enabled
  // If the object is undefined we don't change the enableAssemblyInstructions variable
  if (disabledFeatures && disabledFeatures.assemblyInstructions) {
    enableAssemblyInstructions = false;
  }

  useEffect(() => {
    dispatch(
      setPrintOptions({ ...printOptions, showAssemblyOverview: enableAssemblyInstructions })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableAssemblyInstructions]);

  useEffect(() => {
    if (images?.length === 0) {
      coreApi.ipexViewer.on_current_scene_ready?.().then(() => {
        captureVPCImages();
      });
    }
    // Do not by any means add the dependencies!!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contentToPrint = [
    printOptions.productList && PrintableContentEnum.productListTab,
    printOptions.design && PrintableContentEnum.imagesTab,
    PrintableContentEnum.securityTab,
    printOptions.assemblyOverview && PrintableContentEnum.customPrintContent
  ] as PrintableContentEnum[];

  useEffect(() => {
    const onBeforePrint = async (event: any) => {
      if (event.type === 'printsummary') {
        // Add a small delay to ensure content is rendered
        if (printOptions.assemblyOverview) {
          // If assembly overview is enabled, add a delay to allow rendering
          setTimeout(() => {
            window.print();
          }, 300); // 300ms should be enough in most cases
        } else {
          window.print();
        }
      }
    };
    const onKeyDown = (event: any) => {
      if ((event.ctrlKey || event.metaKey) && event.keyCode === 80) {
        // Ctrl+P shortcut detected (or ⌘+P in Mac)
        event.preventDefault();
        event.stopImmediatePropagation();
        dispatch(showPrintQuestionSheet(true));
      }
    };

    // beforeprint callback does not finish before window.print shows the
    // print modal. So instead we use a custom event when triggering print
    // programmatically.
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('printsummary', onBeforePrint);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('printsummary', onBeforePrint);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (salesSystem !== SalesSystemEnum.isell) {
      const navisionExport = new NavisionExport();
      navisionExport.connect();
      navisionExport.setShoppingItems(shoppingItems);
      return () => navisionExport.disconnect();
    }
  }, [salesSystem, shoppingItems, shoppingProducts]);

  const showTippingFurnitureWarning = rangeIsSmastad() || rangeIsIvar() || rangeIsBror();
  const outerMeasurements = coreApi.entities.getOuterMeasurements();
  const productDescription = getMeasurements(
    coreApi.entities.getOuterMeasurements(),
    useMetricMeasures,
    formatMessage,
    translations
  );

  const designLink = getDesignLink(storeId, vpcCodeToShow);

  const configuration = createDesignConfiguration(coreApi, currentProppingAgeGroup, wallShape);

  const insightsCombinationFormat = coreApi.getInsightsCombinationFormat(
    productsInScene,
    isProduction(),
    kompisSettings.dexfApplicationId
  );

  const serviceOptions = {
    ...kompisSettings,
    redirectUri: stripURLOfQueryParameters(['state', 'code'])
  };

  const configurationOptions: ISummaryPageControllerDesignConfiguration = {
    // The description will be displayed and used to describe the saved design in e.g. component responsible for listing and opening designs.
    configuration: configuration,
    images: vpcImages,
    name: rangeName,
    description: productDescription, //vpc alt text
    versionInfo: formatMessage({ id: 'storageone.storageSolution' }),
    insightsCombinationFormat,
    designId: designId ? designId : undefined
  };

  const mainDesignImage = vpcImages.find(img => img.typeCode === 'main-design-image')?.image;

  const shoppingItemsWithGlobalId = shoppingItems.map(item => ({
    globalItemNo: [item.type, item.id].join('-'),
    ...item
  }));

  const addonItems = shoppingItemsWithGlobalId
    .map(item => item.globalItemNo)
    .filter(globalId => productsInScene.find(p => p.globalItemNo === globalId)?.meta?.addon);

  const normalItems = shoppingItemsWithGlobalId
    .map(item => item.globalItemNo)
    .filter(globalId => !addonItems.includes(globalId));

  useEffect(() => {
    const mappedConfiguration = getMappedConfiguration(configuration, prevSavedConfiguration);
    if (!mappedConfiguration) {
      setDesignSaved(true);
    } else {
      setDesignSaved(false);
    }
  }, [configuration, prevSavedConfiguration]);

  //Added because when you get redirected back to the summary page after logging in it wont have time to take a
  //new screenshot before the solution is saved to profile making the thumbnail showing the IKEA man instead
  if (!e2e() && !configurationOptions.images?.length) {
    return;
  }

  return (
    <RtlStyles>
      <ContentMargins
        styles={{
          marginBottom: '2rem',
          width: '100%',
          minHeight: '100%'
        }}
      >
        <Page
          data-testid="summary-page"
          data-summary-shoppingitems={JSON.stringify(
            shoppingItems.map(({ id, type, quantity }) => ({ id, type, quantity }))
          )}
        >
          <Header
            visible={visible}
            showLogo={!isKiosk()}
            showBackButton={!hideBackButton}
            backButtonTarget={
              backButtonTarget
                ? backButtonTarget
                : configurationPossible
                ? AppRoutes.PLANNER
                : AppRoutes.LANDING_PAGE
            }
          >
            {!isMobile() && <ShoppingButtons />}
            <Menu pl="xs" />
          </Header>
          {!configurationPossible && (
            <WarningBanner
              mt="s"
              data-testid="summary-warning-banner"
              title={formatMessage({ id: 'storageone.summaryWarningCannotOpenTitle' })}
              content={formatMessage(
                { id: 'storageone.summaryWarningCannotOpenText' },
                { range: rangeName }
              )}
            />
          )}

          <Box>
            <PageContainer onScroll={onScroll}>
              <KompisSummaryPageController
                productName={rangeName}
                // If providing a design config, the component will save it as a VPC. We should use this later when we are not always
                // creating a VPC before navigating to summary page.
                designConfiguration={configurationOptions}
                // If sending in VPC code component will not save it again or use the provided design configuration.
                vpcCode={vpcCodeToShow}
                measurementSystem={
                  useMetricMeasures ? MeasurementSystemEnum.metric : MeasurementSystemEnum.imperial
                }
                productDescription={productDescription}
                productMeasures={ProductRowProductMeasureEnum.short}
                configurationMeasures={SummaryPageConfigurationMeasureEnum.short}
                shoppingItems={updatedShoppingItems || shoppingItems}
                serviceOptions={serviceOptions}
                kiosk={isKiosk()}
                collectableProducts={isMobile()}
                designLink={designLink}
                screenshotUrl={
                  fromConfigurationLoader || !configurationPossible
                    ? vpcImage
                    : mainDesignImage
                    ? mainDesignImage
                    : images.length > 0
                    ? images[0]
                    : null
                }
                screenshotAlt={formatMessage({ id: 'storageone.storageSolution' })}
                onProductListRemove={(event: CustomEvent) => {
                  setUpdateShoppingItems(event.detail.shoppingItems);
                }}
                onDesignSavedToProfile={(event: CustomEvent) => {
                  if (event.type === 'designSavedToProfile') {
                    dispatch(saveDesignId(event.detail.designId));
                  }
                }}
                onStateChange={event => {
                  if (event.detail.state.translations && !enableSaveToProfile) {
                    event.detail.state.translations.share = formatMessage({
                      id: 'storageone.saveForLater'
                    });
                    event.detail.state.translations.shareDesignCardDefaultTitle = formatMessage({
                      id: 'storageone.saveForLater'
                    });
                  }

                  const newVpcCode = event.detail.state.vpcCode;

                  if (newVpcCode && vpcCode !== newVpcCode) {
                    dispatch(saveVpcCode(newVpcCode));
                  }

                  const oldProducts = event.detail.oldState.shoppingProducts as
                    | IShoppingProduct[]
                    | null;
                  const newProducts = event.detail.state.shoppingProducts as
                    | IShoppingProduct[]
                    | null;

                  if (!oldProducts || !newProducts) return;

                  oldProducts.forEach(product => {
                    const newProductQuantity =
                      newProducts.find(p => p.product.itemId === product.product.itemId)
                        ?.quantity ?? 0;

                    const amountDiff = product.quantity - newProductQuantity;

                    if (amountDiff) {
                      const addons = coreApi.entities.getByComponent(AddonComponent.id);

                      for (let i = 0; i < amountDiff; i++) {
                        const addon = addons.find(
                          entity =>
                            entity.getComponent<ProductInfoComponent>(ProductInfoComponent.id)
                              ?.globalArticleNbr === product.product.itemId
                        );

                        addon && coreApi.entities.delete(addon);
                      }

                      setLocalScene();
                    }
                  });
                }}
                images={
                  !configurationPossible || !images.length
                    ? null
                    : images.map((i: any) => ({
                        src: i,
                        alt: formatMessage({ id: 'storageone.storageSolution' }),
                        productImage: false
                      }))
                }
                showAssistanceCard={bookAppointmentEnabled}
                showSaveDesign={enableSaveToProfile}
                printHoistTarget={document.body}
                securityInformation={
                  showTippingFurnitureWarning
                    ? {
                        title: formatMessage({ id: 'storageone.summaryTabSafetyHeader' }),
                        description: formatMessage({
                          id: 'storageone.summaryTabSafetyPreamble'
                        }),
                        imageSrc: SummarySafetyInstructions,
                        imageAlt: formatMessage({
                          id: 'storageone.summaryTabSafetyImageDescription'
                        })
                      }
                    : null
                }
                measurements={
                  outerMeasurements
                    ? {
                        height: adjustMeasurementsSystemSummary(outerMeasurements.height),
                        width: adjustMeasurementsSystemSummary(outerMeasurements.width),
                        depth: adjustMeasurementsSystemSummary(outerMeasurements.depth)
                      }
                    : null
                }
                showTotalPackageWeight
                onTabChange={e => {
                  if (e.detail.selectedTab.startsWith('customTab')) {
                    setAssemblyTabTriggered(true);
                  }
                }}
                customTabButtons={
                  enableAssemblyInstructions
                    ? [
                        {
                          label: rangeIsKnoxhult()
                            ? formatMessage({ id: 'storageone.kitchenDrawingTitle' })
                            : formatMessage({ id: 'storageone.assemblyInstructions' })
                        }
                      ]
                    : null
                }
                onTrackAnalytics={eventData =>
                  trackAnalytics(eventData, vpcCode, shoppingItemsWithPrice, currencyCode)
                }
                designSaved={designSaved}
                defaultSortBy={SortListOptionsEnum.aisleLocation}
                assistanceCardApplicationName={bookAppointmentEnabled ? getRange() : null}
                showAssemblyServices={useAssemblyService}
                contentToPrint={contentToPrint}
                /** The code in onPrintButtonClick and onPrintQrCodeRendered is needed if you have the QR code in print, but don't generate the VPC code until print happens.
                 * This will make sure that the QR code is rendered before opening print when the designLink is generated on print. */
                onPrintButtonClick={() => {
                  // Do this in all places where you trigger print
                  if (designLink) {
                    window.print();
                  }
                }}
                productListSections={[
                  {
                    label: formatMessage({ id: 'storageone.summaryAddonsSectionLabel' }),
                    products: addonItems
                  },
                  {
                    label: formatMessage({ id: 'storageone.summaryDesignSectionLabel' }),
                    products: normalItems
                  }
                ]}
                productListRemovable={addonItems}
                experimental={{
                  heroImageCarousel: newSummaryPageDesign
                }}
              >
                {enableAssemblyInstructions && (
                  <div slot="custom-tab-1">
                    {assemblyTabTriggered &&
                      (rangeIsKnoxhult() ? (
                        <AssemblyInstructionsKitchen
                          productsInScene={productsInScene}
                          shouldGenerate={true}
                        />
                      ) : (
                        <AssemblyInstructions
                          goodToKnow={goodToKnow}
                          productsInScene={productsInScene}
                          shouldGenerate={true}
                        />
                      ))}
                    {getRange() === 'platsa' && <PlatsaSoftClosingAssemblyInstruction />}
                  </div>
                )}
                {enableAssemblyInstructions && printOptions.assemblyOverview && (
                  <div slot="custom-print-content">
                    {rangeIsKnoxhult() ? (
                      <AssemblyInstructionsKitchen
                        productsInScene={productsInScene}
                        shouldGenerate={true}
                      />
                    ) : (
                      <AssemblyInstructions
                        goodToKnow={goodToKnow}
                        productsInScene={productsInScene}
                        shouldGenerate={false}
                      />
                    )}
                    {getRange() === 'platsa' && <PlatsaSoftClosingAssemblyInstruction />}
                  </div>
                )}
              </KompisSummaryPageController>
            </PageContainer>
          </Box>
          <ExportScene
            vpcCode={vpcCodeToShow ? vpcCodeToShow : ''}
            configurationImage={images[0]}
          />
        </Page>
      </ContentMargins>
    </RtlStyles>
  );
};

export default Summary;
